import { createStyles, FormControl, FormControlLabel, FormLabel, IconButton, makeStyles, Radio, RadioGroup, Theme } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { ChartCommonProps, IconButtonWrapper } from '../Common';
import CloseIcon from '@material-ui/icons/Close';
import { convertStringNumberToNumber, formatNumber, GetArrow, getMaxTickLimit, getLabels, GetLeadsGroupBy, getSuggestedMaxTick } from '../../helpers/ChartHelper';
import gql from 'graphql-tag';
import { useQuery } from 'react-apollo';
import { Bar } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Scale,
    Tick,
    Chart,
    ChartEvent,
    ActiveElement,
} from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import { SelectedItemProps } from '../../home/MattersByStatusChart';
import { ChartFilter } from '../../home/ChartComponents';
import { useTranslation } from 'react-i18next';
import { TranslationTextList } from '../../locales/common';
import { ChartInfo } from '../../components/ChartInfo';

import { RV_CHART_CLASS, RV_CHART_COLORS, isChartDataEmpty, rvBarDatasetStyle, rvChartAnimation, rvCategoryAxisStyle, rvLegendLabels, rvTooltipStyle, rvValueAxisStyle } from '../../home/chartTheme';
import { ChartHeading } from '../../home/ChartHeading';
import { ChartLimitNote } from '../../home/ChartLimitNote';
import { ChartSkeleton } from '../../components/loading/ChartSkeleton';
import { AnimatedNumber } from '../../home/AnimatedNumber';
import { ChartEmptyState } from '../../home/ChartEmptyState';
import AccessTimeOutlinedIcon from '@material-ui/icons/AccessTimeOutlined';
ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title, Tooltip, Legend, zoomPlugin);

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            position: 'relative',
            paddingBottom: '10px',
            flex: 1,
            display: 'flex',
            flexFlow: 'column',
            // minHeight: '100%',
            maxWidth: '100%',
            maxHeight: '100%',
            height: '100%',
            textAlign: 'center',
            '@media print': {
                display: 'flex',
                flexFlow: 'column',
                alignItems: 'center',
                boxShadow: 'none !important',
            },
        },
        popover: {},
        paper: {},
        radioGroup: {
            flexFlow: 'row wrap',
            justifyContent: 'center',
        },
        closeIcon: {
            color: 'rgb(0, 120, 212)',
        },
        titleContainer: {
            display: 'flex',
            flexFlow: 'column',
            alignItems: 'center',
            padding: '15px 15px 0px 15px',
            fontSize: '16px',
            fontWeight: 600,
        },
        h3: {
            paddingTop: '10px',
        },
        barContainer: {
            padding: '5px 15px 15px 15px',
            minHeight: '300px',
            flex: 1,
            display: 'flex',
            flexFlow: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            maxHeight: '350px',
            maxWidth: 'auto',
            position: 'relative',
            '@media (max-width: 533px)': {
                maxWidth: '350px',
                margin: '0 auto',
            },
            '& .titleWrapper': {
                textAlign: 'center',
                display: 'inline-block',
                width: '100%',
                padding: '15px',
                '& .title': {
                    fontSize: '18px',
                    fontWeight: 600,
                    display: 'block',
                    marginBottom: '15px',
                },
                '& .value': {
                    fontSize: '24px',
                    fontWeight: 600,
                    display: 'block',
                    marginBottom: '15px',
                },
                '& .secondary-title': {
                    fontSize: '16px',
                    fontWeight: 400,
                    display: 'block',
                    marginBottom: '15px',
                },
            },
        },
        loadingWrapper: {
            padding: '20px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
        },
        title: {
            fontSize: 16,
            fontWeight: 600,
            fontFamily: 'inherit',
            marginBottom: 0,
        },
        totalDiv: {
            cursor: 'pointer',
            paddingRight: 5,
        },
        total: {
            fontSize: '2.5rem',
            padding: '10px 0',
            fontWeight: 600,
            cursor: 'pointer',
        },
        span: {
            fontSize: '1rem',
        },
        footer: {
            fontSize: '1rem',
            textAlign: 'center',
        },
        helpIcon: {
            position: 'absolute',
            left: '5px',
            top: '5px',
            '@media print': {
                display: 'none !important',
            },
        },
        formControl: {
            display: 'flex',
            flexFlow: 'row wrap',
            alignItems: 'center',
            justifyContent: 'center',
        },
        formLabel: {
            marginRight: '10px',
        },
    }),
);

export enum LeadsByType {
    CREATED = 0,
    ENQUIRY,
}

interface NewLeads7DaysProps extends SelectedItemProps, ChartCommonProps {
    chartFilter?: ChartFilter;
    groupBy: 'AreaOfLaw' | 'Source';
}

interface ChartState {
    options: LeadsByType;
    groupedByCount: number;
    groupedBy: string[];
    last7DaysCount: number[];
    previous7DaysCount: number[];
    last7DaysTotalCount: number;
    previous7DaysTotalCount: number;
}

export const NewLeadsInLast7DaysByGroupChart: React.FC<NewLeads7DaysProps> = (props) => {
    const classes = useStyles();

    const { t } = useTranslation() as {
        t: (key: keyof TranslationTextList) => string;
    };
    
    // tslint:disable-next-line:no-any
    const chartRef = React.useRef<any>();

    const [state, setState] = useState<ChartState>({
        options: LeadsByType.CREATED,
        groupedByCount: 0,
        groupedBy: [],
        last7DaysCount: [],
        previous7DaysCount: [],
        last7DaysTotalCount: 0,
        previous7DaysTotalCount: 0,
    });

    const newLeadsQuery = useQuery<NewLeads7DaysData>(NEW_LEADS_7DAYS, {
        variables: {
            groupBy: props.groupBy,
            entityType: props.chartFilter?.entityType,
            entityGuid: props.chartFilter?.entityGuid,
            // responsibility: props.chartFilter?.responsibility,
        },
        fetchPolicy: 'cache-and-network',
    });

    useEffect(() => {
        setState((prevState) => {
            let groupedByCount: number = 0;
            let groupedBy: string[] = [];
            let last7DaysCount: number[] = [];
            let previous7DaysCount: number[] = [];
            let last7DaysTotalCount: number = 0;
            let previous7DaysTotalCount: number = 0;

            if (!newLeadsQuery.loading && !newLeadsQuery.error && newLeadsQuery.data && newLeadsQuery.data.statistics && newLeadsQuery.data.statistics.lead) {
                
                const { 
                    newLeadsInLast7DaysByInitialDate, 
                    newLeadsInLast7DaysByCreationDate 
                } = newLeadsQuery.data.statistics.lead.newLeadsInLast7Days;

                if (state.options === LeadsByType.ENQUIRY) {
                    groupedByCount = newLeadsInLast7DaysByInitialDate.groupedByCount;
                    groupedBy = newLeadsInLast7DaysByInitialDate.groupedLeadsForLast7Days.map((l) => {
                        return l.groupedBy;
                    });
                    last7DaysCount = newLeadsInLast7DaysByInitialDate.groupedLeadsForLast7Days.map((l) => {
                        return l.last7DaysCount;
                    });
                    previous7DaysCount = newLeadsInLast7DaysByInitialDate.groupedLeadsForLast7Days.map((l) => {
                        return l.previous7DaysCount;
                    });

                    last7DaysTotalCount = newLeadsInLast7DaysByInitialDate.leads7DaysTotalCount.last7DaysTotalCount;
                    previous7DaysTotalCount = newLeadsInLast7DaysByInitialDate.leads7DaysTotalCount.previous7DaysTotalCount;
                } else {
                    groupedByCount = newLeadsInLast7DaysByCreationDate.groupedByCount;
                    groupedBy = newLeadsInLast7DaysByCreationDate.groupedLeadsForLast7Days.map((l) => {
                        return l.groupedBy;
                    });
                    last7DaysCount = newLeadsInLast7DaysByCreationDate.groupedLeadsForLast7Days.map((l) => {
                        return l.last7DaysCount;
                    });
                    previous7DaysCount = newLeadsInLast7DaysByCreationDate.groupedLeadsForLast7Days.map((l) => {
                        return l.previous7DaysCount;
                    });

                    last7DaysTotalCount = newLeadsInLast7DaysByCreationDate.leads7DaysTotalCount.last7DaysTotalCount;
                    previous7DaysTotalCount = newLeadsInLast7DaysByCreationDate.leads7DaysTotalCount.previous7DaysTotalCount;
                }
            }

            return {
                ...prevState,
                groupedByCount,
                groupedBy,
                last7DaysCount,
                previous7DaysCount,
                last7DaysTotalCount,
                previous7DaysTotalCount,
            };
        });
        // tslint:disable-next-line: align
    }, [newLeadsQuery.data, newLeadsQuery.loading, newLeadsQuery.error, state.options, newLeadsQuery]);

    const onCloseChart = () => {
        if (props.onCloseChart) {
            props.onCloseChart(`IsNewLeadsInLast7DaysBy${props.groupBy}ChartOpen`);
        }
    };

    const onChangeRadio = (event: React.ChangeEvent<HTMLInputElement>) => {
        const fieldName = event.target.name;
        setState((prevState) => {
            return {
                ...prevState,
                // tslint:disable-next-line: radix
                [fieldName]: parseInt((event.target as HTMLInputElement).value),
            };
        });
    };

    const onPan = (context: { chart: Chart }) => {
        chartRef.current.options.scales.y.min = 0;
        chartRef.current.update();
    };

    const onHover = (event: ChartEvent, elements: ActiveElement[], chart: Chart) => {
        (event?.native?.target as HTMLElement).style.cursor = elements.length > 0 ? 'pointer' : 'auto';
    };

    if (newLeadsQuery.loading) {
        return (
            <ChartSkeleton variant="bars" />
        );
    }

    return (
        <div className={`${classes.root} ${RV_CHART_CLASS}${isChartDataEmpty(state.previous7DaysCount, state.last7DaysCount) ? ' rv-chart--empty' : ''}`}>
            <IconButtonWrapper aria-label="close">
                <ChartInfo 
                    description={props.description}
                    fontSize={'medium'}
                    className={`${classes.helpIcon} rv-chart-help`}
                />
                <IconButton aria-label="close" onClick={onCloseChart} size="small">
                    <CloseIcon className={classes.closeIcon} />
                </IconButton>
            </IconButtonWrapper>
            <div className={classes.titleContainer}>
                <ChartHeading icon={<AccessTimeOutlinedIcon />} title={<>New {t('Enquiries')} in the last 7 Days</>} />
                <div className={`${classes.total} h2`}>
                    <AnimatedNumber value={state.last7DaysTotalCount} format={(amount) => formatNumber(amount)} />
                    {GetArrow(state.last7DaysTotalCount, state.previous7DaysTotalCount)}
                </div>
                <div className={classes.title}>{formatNumber(state.previous7DaysTotalCount)} new {t('enquiries')} in the 7 days before that</div>
                <div className={classes.h3}>New {t('Enquiries')} by {GetLeadsGroupBy(props.groupBy)}</div>
            </div>
            <div className={classes.barContainer}>
                <Bar
                    ref={chartRef}
                    data={{
                        labels: getLabels(state.groupedBy),
                        datasets: [
                            {
                                label: 'Previous 7 Days',
                                ...rvBarDatasetStyle(RV_CHART_COLORS.primarySoft),
                                data: state.previous7DaysCount,
                            },
                            {
                                label: 'Current 7 Days',
                                ...rvBarDatasetStyle(RV_CHART_COLORS.primary),
                                data: state.last7DaysCount,
                            },
                        ],
                    }}
                    options={{
                        onHover: onHover,
                        indexAxis: 'y' as const,
                        animation: rvChartAnimation(),
                        elements: { bar: { borderWidth: 0 } },
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                            x: {
                                grid: rvValueAxisStyle.grid,
                                beginAtZero: true,
                                suggestedMax: getSuggestedMaxTick([...state.last7DaysCount, ...state.previous7DaysCount]),
                                min: 0,
                                ticks: {
                                    ...rvValueAxisStyle.ticks,
                                    maxTicksLimit: getMaxTickLimit([...state.last7DaysCount, ...state.previous7DaysCount]),
                                    autoSkip: false,
                                    callback: function (this: Scale, tickValue: number, index: number, ticks: Tick[]) {
                                        return `${formatNumber(tickValue)}`;
                                    },
                                },
                            },
                            y: {
                                grid: rvCategoryAxisStyle.grid,
                                ticks: {
                                    ...rvCategoryAxisStyle.ticks,
                                    autoSkip: false,
                                },
                            },
                        },
                        plugins: {
                            legend: {
                                position: 'bottom' as const,
                                display: true,
                                labels: rvLegendLabels,
                            },
                            title: {
                                display: false,
                                text: `${t('Enquiries')} Opened in the last 7 days: ${formatNumber(state.last7DaysTotalCount)}`,
                            },
                            zoom: {
                                pan: {
                                    enabled: false,
                                    onPan: onPan,
                                },
                                zoom: {
                                    wheel: {
                                        enabled: false,
                                    },
                                },
                            },
                            tooltip: {
                                ...rvTooltipStyle,
                                callbacks: {
                                    // tslint:disable-next-line: no-any
                                    label: function (context: any) {
                                        const {
                                            // dataIndex,
                                            // dataset,
                                            // label,
                                            formattedValue,
                                        } = context;

                                        const currentValue = convertStringNumberToNumber(formattedValue);

                                        return `${t('Enquiries')} ${formatNumber(currentValue)}`;
                                    },
                                },
                            },
                        },
                    }}
                />
                {isChartDataEmpty(state.previous7DaysCount, state.last7DaysCount) && (
                    <ChartEmptyState title="No data" hint="Nothing to show for this selection." />
                )}
            </div>
            <FormControl component="div" className={classes.formControl}>
                <FormLabel component="legend" className={classes.formLabel}>
                    Show
                </FormLabel>
                <RadioGroup name="options" value={state.options} onChange={onChangeRadio} className={classes.radioGroup}>
                    <FormControlLabel value={LeadsByType.CREATED} control={<Radio color="primary" />} label="Date Added" />
                    <FormControlLabel value={LeadsByType.ENQUIRY} control={<Radio color="primary" />} label={t('Enquiry Date')} />
                </RadioGroup>
            </FormControl>
            <div className={classes.footer}>{state.groupedByCount > 10 ? <ChartLimitNote>The top 10 are shown</ChartLimitNote> : undefined}</div>
        </div>
    );
};

export interface CurrencySymbolData {
    options: Options;
}

export interface Options {
    currencySymbol: CurrencySymbol;
}

export interface CurrencySymbol {
    symbol: string;
}

export const CURRENCY_SYMBOL_QUERY = gql`
    query CurrencySymbol {
        options {
            currencySymbol {
                symbol
            }
        }
    }
`;

export interface NewLeads7DaysData {
    statistics: Statistics;
}

export interface Statistics {
    lead: Leads;
}

export interface Leads {
    newLeadsInLast7Days: NewLeadsByPeriod;
}

export interface NewLeadsByPeriod {
    newLeadsInLast7DaysByInitialDate: LeadsByGroup;
    newLeadsInLast7DaysByCreationDate: LeadsByGroup;
}

export interface LeadsByGroup {
    groupedByCount: number;
    groupedLeadsForLast7Days: NewLead[];
    leads7DaysTotalCount: LeadsByGroupSummary;
}

export interface LeadsByGroupSummary {
    last7DaysTotalCount: number;
    previous7DaysTotalCount: number;
}

export interface NewLead {
    groupedBy: string;
    last7DaysCount: number;
    previous7DaysCount: number;
}

const NEW_LEADS_7DAYS = gql`
    fragment NewLeadsInLast7DaysByGroupFragment on NewLeadsInLast7DaysByGroup {
        leads7DaysTotalCount {
            last7DaysTotalCount
            previous7DaysTotalCount
        }
        groupedByCount
        groupedLeadsForLast7Days {
            last7DaysCount
            previous7DaysCount
            groupedBy
        }
    }

    query leads($groupBy: NewLeadsGroupBy, $entityType: Int, $entityGuid: String, $include: Boolean) {
        statistics {
            lead {
                newLeadsInLast7Days(groupBy: $groupBy, entityType: $entityType, entityGuid: $entityGuid, include: $include) {
                    newLeadsInLast7DaysByInitialDate {
                        ...NewLeadsInLast7DaysByGroupFragment
                    }
                    newLeadsInLast7DaysByCreationDate {
                        ...NewLeadsInLast7DaysByGroupFragment
                    }
                }
            }
        }
    }
`;
